import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { listPlans, plansByHotelKey, type MonthlyPlan } from '../features/plans/api.js';

const HU_MONTH_LONG = [
  'január',
  'február',
  'március',
  'április',
  'május',
  'június',
  'július',
  'augusztus',
  'szeptember',
  'október',
  'november',
  'december',
];

function HotelCalendarPage() {
  const { hotelId } = Route.useParams();
  const navigate = useNavigate();
  const [year, setYear] = useState(() => new Date().getUTCFullYear());

  const plans = useQuery<MonthlyPlan[]>({
    queryKey: plansByHotelKey(hotelId),
    queryFn: () => listPlans(hotelId),
  });

  // Index plans for the selected year by month (1..12). A hotel can have at
  // most one plan per (year, month) — the unique constraint guarantees it.
  const plansByMonth = useMemo(() => {
    const m = new Map<number, MonthlyPlan>();
    for (const p of plans.data ?? []) {
      if (p.year === year) m.set(p.month, p);
    }
    return m;
  }, [plans.data, year]);

  const currentYm = useMemo(() => {
    const now = new Date();
    return { year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 };
  }, []);

  return (
    <div className="space-y-4">
      {/* Year switcher */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] px-4 py-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setYear((y) => y - 1)}
            aria-label="Előző év"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--ink-700)] hover:bg-[var(--app-hover)]"
          >
            ←
          </button>
          <span className="min-w-[80px] text-center text-h2 font-semibold tn text-[var(--ink-700)]">
            {year}
          </span>
          <button
            type="button"
            onClick={() => setYear((y) => y + 1)}
            aria-label="Következő év"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--app-divider)] bg-[var(--app-surface)] text-[var(--ink-700)] hover:bg-[var(--app-hover)]"
          >
            →
          </button>
        </div>
        <p className="text-meta text-[var(--fg-2)]">
          Az éves áttekintés a tervvel rendelkező hónapokat mutatja. Kattints egy hónapra a
          megnyitáshoz.
        </p>
      </div>

      {/* 12-month grid */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {HU_MONTH_LONG.map((name, idx) => {
          const month = idx + 1;
          const plan = plansByMonth.get(month);
          const isCurrent = currentYm.year === year && currentYm.month === month;
          return (
            <MonthTile
              key={month}
              name={name}
              plan={plan}
              isCurrent={isCurrent}
              onOpen={() =>
                void navigate({
                  to: '/app/plans/$hotelId/$year/$month',
                  params: { hotelId, year: String(year), month: String(month) },
                })
              }
              onCreate={() =>
                void navigate({
                  to: '/app/plans/new',
                  search: { hotelId, year, month },
                })
              }
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Inclusive day count between two YYYY-MM-DD strings — used as a lightweight
 * "intensity" signal for the heat tint (longer periods → stronger tint).
 */
function periodDays(plan: MonthlyPlan): number {
  const ms =
    new Date(`${plan.endDate}T00:00:00Z`).getTime() -
    new Date(`${plan.startDate}T00:00:00Z`).getTime();
  return Math.round(ms / 86_400_000) + 1;
}

function MonthTile({
  name,
  plan,
  isCurrent,
  onOpen,
  onCreate,
}: {
  name: string;
  plan: MonthlyPlan | undefined;
  isCurrent: boolean;
  onOpen: () => void;
  onCreate: () => void;
}) {
  if (!plan) {
    return (
      <button
        type="button"
        onClick={onCreate}
        className={`group flex min-h-[112px] flex-col rounded-lg border border-dashed p-3 text-left transition-colors hover:border-[var(--brand-400)] hover:bg-[var(--brand-50)] ${
          isCurrent ? 'border-[var(--brand-400)]' : 'border-[var(--app-divider)]'
        }`}
      >
        <div className="flex items-center justify-between">
          <span className="text-body font-semibold capitalize text-[var(--ink-700)]">{name}</span>
          {isCurrent && (
            <span className="rounded-full bg-[var(--brand-100)] px-1.5 py-0.5 text-meta font-semibold text-[var(--brand-800)]">
              most
            </span>
          )}
        </div>
        <span className="mt-1 text-meta text-[var(--fg-faint)]">Nincs terv</span>
        <span className="mt-auto text-meta text-[var(--fg-muted)] opacity-0 transition-opacity group-hover:opacity-100">
          + Terv létrehozása
        </span>
      </button>
    );
  }

  // Heat tint: clamp the period length to a small 0..1 scale and mix the
  // brand colour into the surface. A whole month (~30 nap) reaches full tint.
  // color-mix keeps us on the real --brand-500 token (no hand-coded rgb).
  const intensity = Math.min(1, periodDays(plan) / 30);
  const tintPct = Math.round(10 + intensity * 35); // 10% .. 45% brand

  return (
    <button
      type="button"
      onClick={onOpen}
      style={{
        background: `color-mix(in srgb, var(--brand-500) ${tintPct}%, var(--app-surface))`,
      }}
      className={`group flex min-h-[112px] flex-col rounded-lg border p-3 text-left transition-transform hover:-translate-y-0.5 hover:shadow-md ${
        isCurrent ? 'border-[var(--brand-500)]' : 'border-[var(--brand-200)]'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-body font-semibold capitalize text-[var(--ink-700)]">{name}</span>
        {isCurrent && (
          <span className="rounded-full bg-[var(--brand-100)] px-1.5 py-0.5 text-meta font-semibold text-[var(--brand-800)]">
            most
          </span>
        )}
      </div>
      <div className="mt-1.5 text-meta text-[var(--ink-700)]">
        <span className="font-semibold tn">{plan.startDate.slice(5)}</span>
        <span className="mx-1 text-[var(--fg-faint)]">→</span>
        <span className="font-semibold tn">{plan.endDate.slice(5)}</span>
      </div>
      <span className="mt-1 text-meta text-[var(--fg-2)]">{periodDays(plan)} nap</span>
      <span className="mt-auto pt-2 text-meta font-medium text-[var(--brand-700)] opacity-0 transition-opacity group-hover:opacity-100">
        Megnyitás →
      </span>
    </button>
  );
}

export const Route = createFileRoute('/app/hotels/$hotelId/calendar')({
  component: HotelCalendarPage,
});
